import { useMemo, useState } from 'react';
import YoutubeModalComponent from '@/components/admin-alineaciones-probables/shared/YoutubeModalComponent';
import DonquijoteLogo from '@/components/shared/icons/DonquijoteLogo';
import YoutubeIcon from '@/components/shared/icons/youtube-icon';
import { Box, Button, Typography, Tabs, Tab, Avatar, Tooltip } from '@mui/material';
import DonquiTipsModal from './DonquiTipsModal';
import NewIcon from '@/components/shared/icons/NewIcon';
import CustomSectionWrapper from '@/DesignSystem/wrappers/CustomSectionWrapper';
import { useAlineacionesContext } from '../hooks/useAlineacionesContext';
import CustomImageComponent from '@/components/shared/custom/custom-image';
import SocialNetworkLinks from '@/components/shared/social-networks/SocialNetworkLinks';

const DonquiAction = ({ label, icon, onClick, disabled = false, newIcon = false }) => {
    return (
        <Button
            onClick={onClick}
            disabled={disabled}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                padding: '3px 6px',
                fontSize: 12,
                border: '1px solid',
                borderColor: 'border.main',
                borderRadius: 1,
                textTransform: 'none',
                color: 'text.primary',
                minHeight: 28,
            }}
        >
            {icon}
            {label}
            {newIcon && <NewIcon width={14} height={14} />}
        </Button>
    );
};

// Componente para el label de la tab del editor
const EditorTabLabel = ({ editor, showText = false }) => {
    const content = editor?.image ? (
        <CustomImageComponent
            src={editor.image}
            alt={editor.nickname}
            width={24}
            height={24}
            style={{ borderRadius: '50%' }}
        />
    ) : (
        <Avatar
            sx={{
                width: 24,
                height: 24,
                fontSize: 12,
                bgcolor: 'primary.main',
            }}
        >
            {editor?.nickname?.charAt(0).toUpperCase() || 'E'}
        </Avatar>
    );

    const label = (
        <Box display="flex" alignItems="center" gap={showText ? 1 : 0}>
            {content}
            {showText && (
                <Typography fontSize={13} fontWeight={500}>
                    {editor?.nickname}
                </Typography>
            )}
        </Box>
    );

    // Si no mostramos texto, añadir tooltip
    if (!showText) {
        return (
            <Tooltip title={editor?.nickname || 'Editor'} arrow>
                {label}
            </Tooltip>
        );
    }

    return label;
};

// Componente para mostrar las redes sociales del editor
const EditorSocialNetworks = ({ editor }) => {
    return (
        <Box>
            <SocialNetworkLinks userSocial={editor?.userSocial} compact={true} />
        </Box>
    );
};

// Componente para el contenido de Donqui
const DonquiContent = ({ equipo, ...props }) => {
    const [showYoutubeModal, setShowYoutubeModal] = useState(false);
    const [showPredictionsModal, setShowPredictionsModal] = useState(false);

    const isAnyDonquiTips = useMemo(
        () =>
            equipo?.lineup?.some(
                (player) =>
                    player.isCapitan ||
                    player.isChollo ||
                    player.isAriete ||
                    player.isBufon ||
                    player.isRevulsivo
            ),
        [equipo?.lineup]
    );

    // Redes sociales de Don Quijote (hardcodeadas)
    const donquijoteSocial = {
        twitter: 'DonQuijoteDeLaC',
        youtube: 'DonQuijoteDLaCancha',
        tiktok: 'donquijotedelacancha',
        twitch: 'donquijotedlc',
    };

    const handleDonquiTipsClick = () => {
        setShowPredictionsModal(true);
    };

    const handleDonquiPreviaClick = () => {
        setShowYoutubeModal(true);
    };

    return (
        <>
            <Box display="flex" gap={1} alignItems="center" flexWrap="wrap" {...props}>
                <Box display="flex" gap={1} alignItems="center" justifyContent="space-between">
                    <DonquiAction
                        label="Picks"
                        icon={<DonquijoteLogo width={16} height={16} />}
                        onClick={handleDonquiTipsClick}
                        disabled={!isAnyDonquiTips}
                    />
                    <DonquiAction
                        label="Previa"
                        icon={<YoutubeIcon width={16} height={16} />}
                        onClick={handleDonquiPreviaClick}
                        disabled={!equipo?.youtube}
                    />
                </Box>
                <SocialNetworkLinks userSocial={donquijoteSocial} compact={true} />
            </Box>

            {/* Modales */}
            {showPredictionsModal && (
                <DonquiTipsModal
                    players={equipo?.lineup}
                    open={showPredictionsModal}
                    onClose={() => setShowPredictionsModal(false)}
                />
            )}

            {showYoutubeModal && (
                <YoutubeModalComponent
                    youtubeUrl={equipo?.youtube}
                    open={showYoutubeModal}
                    onClose={() => setShowYoutubeModal(false)}
                />
            )}
        </>
    );
};

const DonquiCardEquipo = ({ equipo, mode, ...props }) => {
    const { editorLineups, setSelectedEditorLineup, selectedEditorLineup } = useAlineacionesContext();

    // Filtrar las alineaciones del equipo actual
    const teamEditorLineups = useMemo(() => {
        if (!equipo?.id) return [];
        return editorLineups?.lineups?.filter((lineup) => lineup.team === equipo.id) || [];
    }, [editorLineups?.lineups, equipo?.id]);

    // Determinar si mostrar texto en las tabs (cuando hay 5 o menos tabs)
    const totalTabs = 1 + teamEditorLineups.length; // 1 de DonquiPicks + editores
    const showText = totalTabs <= 3;

    // Calcular el activeTab basado en el selectedEditorLineup del contexto
    const activeTab = useMemo(() => {
        // Si no hay editor seleccionado, mostrar la tab de DonQuijote (0)
        if (!selectedEditorLineup) return 0;

        // Si el editor seleccionado no es de este equipo, mostrar la tab de DonQuijote (0)
        if (selectedEditorLineup.team !== equipo?.id) {
            // Buscar si hay un editor con el mismo userId en este equipo
            const editorIndex = teamEditorLineups.findIndex(
                (lineup) => lineup.editor?.userId === selectedEditorLineup.editor?.userId
            );

            // Si encontramos el mismo editor en este equipo, seleccionarlo
            if (editorIndex !== -1) {
                return editorIndex + 1; // +1 porque la tab 0 es DonQuijote
            }

            // Si no encontramos el editor, mostrar DonQuijote
            return 0;
        }

        // Si el editor seleccionado es de este equipo, buscar su índice
        const editorIndex = teamEditorLineups.findIndex(
            (lineup) => lineup.editor?.userId === selectedEditorLineup.editor?.userId
        );

        // Si lo encontramos, devolver su posición (+1 por DonQuijote), si no, mostrar DonQuijote
        return editorIndex !== -1 ? editorIndex + 1 : 0;
    }, [selectedEditorLineup, teamEditorLineups, equipo?.id]);

    // Generar título dinámico
    const title = useMemo(() => {
        if (!equipo?.name) return '';

        const baseTitle = `Alineaciones probables de ${equipo.name}`;

        // Si hay un editor seleccionado, agregarlo al título
        if (selectedEditorLineup?.editor?.nickname) {
            const editorName = selectedEditorLineup.editor.nickname;
            return `${baseTitle} - ${editorName}`;
        }

        return baseTitle;
    }, [equipo?.name, selectedEditorLineup]);

    const handleTabChange = (event, newValue) => {
        // Si es la tab 0 (DonquiPicks), limpiar la selección del editor
        if (newValue === 0) {
            setSelectedEditorLineup(null);
        } else {
            // Seleccionar el lineup del editor correspondiente a este equipo
            const selectedLineup = teamEditorLineups[newValue - 1];
            setSelectedEditorLineup(selectedLineup);
        }
    };

    // Validación después de todos los hooks
    if (!equipo || mode !== 'predict') return null;

    return (
        <CustomSectionWrapper color="accent2" title={title}>
            {/* Header con Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        minHeight: 36,
                        '& .MuiTab-root': {
                            minHeight: 36,
                            minWidth: 'auto',
                            px: 0.5,
                            py: 0,
                            fontSize: 13,
                            textTransform: 'none',
                            fontWeight: 500,
                        },
                    }}
                >
                    <Tab
                        label={
                            <Box display="flex" alignItems="center" gap={showText ? 1 : 0}>
                                <DonquijoteLogo width={24} height={24} />
                                {showText && (
                                    <Typography fontSize={13} fontWeight={500}>
                                        Don Quijote
                                    </Typography>
                                )}
                            </Box>
                        }
                        sx={{ minWidth: 'auto' }}
                    />
                    {teamEditorLineups.map((lineup, index) => (
                        <Tab
                            key={lineup.editor?.userId || index}
                            label={<EditorTabLabel editor={lineup.editor} showText={showText} />}
                            sx={{ minWidth: 'auto' }}
                        />
                    ))}
                </Tabs>
            </Box>

            {/* Contenido según la tab activa */}
            <Box>
                {activeTab === 0 && <DonquiContent equipo={equipo} {...props} />}
                {teamEditorLineups.map(
                    (lineup, index) =>
                        activeTab === index + 1 && (
                            <EditorSocialNetworks
                                key={lineup.editor?.userId || index}
                                editor={lineup.editor}
                            />
                        )
                )}
            </Box>
        </CustomSectionWrapper>
    );
};

export default DonquiCardEquipo;
